import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Sparkles, Loader2, ArrowRight } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import { useAuth } from "@/lib/auth";

const renderPickerCta = (plan, busy, user) => {
  if (busy === plan.id) {
    return <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin"/> Starting…</span>;
  }
  if (user?.subscription_tier === plan.id) return "Current plan";
  return <span className="inline-flex items-center gap-1.5">Start trial <ArrowRight className="w-3 h-3"/></span>;
};

const PLANS = [
  {
    id: "starter",
    lookup_key: "grantflow_starter_monthly",
    name: "Starter",
    price: "$29",
    interval: "per month",
    tagline: "Get moving today.",
    features: [
      "Search 12,400+ grants",
      "3 active grants at a time",
      "Save 25 grants",
      "3 AI drafts / month",
      "Deadline reminders",
    ],
  },
  {
    id: "pro",
    lookup_key: "grantflow_pro_monthly",
    name: "Professional",
    price: "$59",
    interval: "per month",
    tagline: "For serious pursuers.",
    highlight: true,
    features: [
      "Everything in Starter",
      "Unlimited applications",
      "Unlimited saved grants",
      "Unlimited AI drafts",
      "Priority match algorithm",
      "Document vault",
    ],
  },
  {
    id: "enterprise",
    lookup_key: "grantflow_enterprise_monthly",
    name: "Enterprise",
    price: "$99",
    interval: "per month",
    tagline: "For teams and multi-orgs.",
    features: [
      "Everything in Professional",
      "5 team seats",
      "Custom grant sources",
      "API access",
      "SSO & 2FA enforcement",
    ],
  },
];

export default function Pricing() {
  const [busy, setBusy] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const startTrial = (plan) => {
    if (!user) {
      // Redirect to signup, remember chosen plan
      sessionStorage.setItem("bg_pending_plan", plan.lookup_key);
      navigate("/signup");
      return;
    }
    setBusy(plan.id);
    api
      .post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      })
      .then((res) => {
        window.location.href = res.data.checkout_url;
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not start checkout");
        setBusy(null);
      });
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="pricing-page">
      <MarketingNav />
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-20">
        <div className="text-center mb-12">
          <div className="label-eyebrow mb-4">Pricing</div>
          <h1 className="font-heading font-light text-5xl md:text-6xl tracking-tighter text-[#1A1A1A]">
            Priced like it should be.
          </h1>
          <p className="text-[#5E5E5E] mt-4 text-lg max-w-2xl mx-auto leading-relaxed">
            Every plan starts with a 7-day free trial. Cancel anytime before day 7 — no charge.
          </p>

          <div className="mt-10 flex flex-col items-center gap-3">
            <Button
              onClick={() => setPickerOpen(true)}
              disabled={busy !== null}
              data-testid="pricing-start-trial-primary"
              className="bg-[#C1654B] text-white hover:bg-[#A0533E] rounded-md px-8 py-6 text-base font-medium shadow-lg shadow-[#C1654B]/20 transition-all hover:shadow-xl hover:shadow-[#C1654B]/30 hover:-translate-y-0.5"
            >
              <Sparkles className="w-4 h-4 mr-2"/>
              Start Your Free 7-Day Trial
              <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
            <p className="text-xs text-[#7A8C77] uppercase tracking-widest font-bold">
              Pick your plan · Cancel anytime · No charge on day 7 if you cancel
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`rounded-lg border p-8 flex flex-col ${
                p.highlight ? "bg-[#1C3F35] text-white border-[#1C3F35] shadow-lg scale-[1.02]" : "bg-white border-[#E6E4DF]"
              }`}
            >
              {p.highlight && (
                <div className="inline-flex items-center gap-1 self-start px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#C1654B] text-white mb-4">
                  <Sparkles className="w-3 h-3"/> Popular
                </div>
              )}
              <h3 className="font-heading text-3xl font-medium">{p.name}</h3>
              <p className={`mt-1 ${p.highlight ? "text-white/70" : "text-[#5E5E5E]"}`}>{p.tagline}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-light">{p.price}</span>
                <span className={p.highlight ? "text-white/60" : "text-[#5E5E5E]"}>/ {p.interval}</span>
              </div>
              <div
                className={`mt-2 inline-flex items-center gap-1.5 self-start px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                  p.highlight ? "bg-white/10 text-white" : "bg-[#1C3F35]/5 text-[#1C3F35]"
                }`}
              >
                <Sparkles className="w-3 h-3"/> Included in 7-day free trial
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-[#C1654B]" : "text-[#1C3F35]"}`}/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {user?.subscription_tier === p.id && (
                <div
                  className={`mt-8 py-3 rounded-md text-center text-sm font-medium ${
                    p.highlight ? "bg-white/10 text-white" : "bg-[#1C3F35]/5 text-[#1C3F35]"
                  }`}
                  data-testid={`plan-${p.id}-current`}
                >
                  Your current plan
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-[#5E5E5E] mt-10">
          All plans billed in USD · Powered by Stripe · Cancel anytime ·{" "}
          <Link to="/refund-policy" className="underline underline-offset-2 hover:text-[#1C3F35]" data-testid="pricing-refund-link">
            Refund &amp; Cancellation Policy
          </Link>
        </p>
      </section>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-white max-w-3xl" data-testid="trial-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light text-3xl md:text-4xl tracking-tighter text-[#1A1A1A]">
              Pick your plan
            </DialogTitle>
            <DialogDescription className="text-[#5E5E5E] text-base">
              Every plan starts with a 7-day free trial. Your card won't be charged until day 8 — cancel anytime before then.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLANS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => startTrial(p)}
                disabled={busy !== null || user?.subscription_tier === p.id}
                data-testid={`trial-picker-${p.id}`}
                className={`text-left rounded-lg border p-5 transition-all ${
                  p.highlight
                    ? "bg-[#1C3F35] text-white border-[#1C3F35] hover:bg-[#1A362D]"
                    : "bg-white border-[#E6E4DF] hover:border-[#1C3F35] hover:shadow-md text-[#1A1A1A]"
                } ${(busy !== null || user?.subscription_tier === p.id) ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {p.highlight && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#C1654B] text-white mb-3">
                    <Sparkles className="w-3 h-3"/> Popular
                  </div>
                )}
                <div className="font-heading text-2xl font-medium">{p.name}</div>
                <div className={`text-xs mt-1 ${p.highlight ? "text-white/70" : "text-[#5E5E5E]"}`}>{p.tagline}</div>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="font-heading text-4xl font-light">{p.price}</span>
                  <span className={`text-sm ${p.highlight ? "text-white/60" : "text-[#5E5E5E]"}`}>/ mo</span>
                </div>
                <div className={`mt-4 text-xs font-bold uppercase tracking-widest ${p.highlight ? "text-[#C1654B]" : "text-[#1C3F35]"}`}>
                  {renderPickerCta(p, busy, user)}
                </div>
              </button>
            ))}
          </div>

          <p className="mt-4 text-xs text-[#5E5E5E] text-center">
            You'll be redirected to Stripe to enter your card. Day-8 charge is the plan price you pick.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
